import { useState, useRef } from "react";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, Save, Camera, Trash2, AlertTriangle, CheckCircle2, KeyRound, UserX } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [photoUrl, setPhotoUrl] = useState(user?.photo_url || "");
  const [role, setRole] = useState(user?.role || "Automation & AI Specialist");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: "error", text: "Photo must be under 2 MB." });
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setPhotoUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    if (!name.trim() || !email.trim()) {
      setMessage({ type: "error", text: "Name and email are required." });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const updated = await api.updateMe({ name, email, photo_url: photoUrl || null, role: role || null });
      setUser(updated);
      setMessage({ type: "success", text: "Profile saved." });
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (newPassword.length < 6) {
      setMessage({ type: "error", text: "New password must be at least 6 characters." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "Passwords do not match." });
      return;
    }
    setChangingPassword(true);
    setMessage(null);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage({ type: "success", text: "Password changed." });
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Password change failed" });
    } finally {
      setChangingPassword(false);
    }
  };

  const deleteAccount = async () => {
    if (!deletePassword) {
      setMessage({ type: "error", text: "Enter your password to delete the account." });
      return;
    }
    setDeleting(true);
    setMessage(null);
    try {
      await api.deleteAccount(deletePassword);
      window.location.href = "/login";
    } catch (e) {
      setMessage({ type: "error", text: e instanceof Error ? e.message : "Delete failed" });
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm ${message.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Camera className="w-5 h-5 text-trofa-700" /> Profile
        </h2>

        <div className="flex items-start gap-6 mb-6">
          <div className="shrink-0">
            <div className="w-24 h-24 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center border border-slate-200">
              {photoUrl ? (
                <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl text-slate-400 font-medium">{name ? name[0].toUpperCase() : "?"}</span>
              )}
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              className="mt-2 text-xs text-trofa-700 hover:text-trofa-800 font-medium"
            >
              Change photo
            </button>
            <input type="file" ref={fileRef} className="hidden" accept="image/*" onChange={handlePhotoSelect} />
          </div>

          <div className="flex-1 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-trofa-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-trofa-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role / Title</label>
              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="Automation & AI Specialist"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-trofa-700"
              />
              <p className="text-xs text-slate-500 mt-1">This appears under your name in the sidebar.</p>
            </div>
            {user?.is_admin && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-trofa-100 text-trofa-800">
                Admin
              </span>
            )}
          </div>
        </div>

        <button
          onClick={saveProfile}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-trofa-700 hover:bg-trofa-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Profile
        </button>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-trofa-700" /> Change Password
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input
            type="password"
            placeholder="Current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-trofa-700"
          />
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-trofa-700"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-trofa-700"
          />
        </div>
        <button
          onClick={changePassword}
          disabled={changingPassword}
          className="inline-flex items-center gap-2 bg-trofa-700 hover:bg-trofa-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {changingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          Change Password
        </button>
      </section>

      <section className="bg-white rounded-xl border border-red-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
          <UserX className="w-5 h-5 text-red-600" /> Delete Account
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          This will permanently delete your account and all associated data. This action cannot be undone.
        </p>

        {!deleteConfirm ? (
          <button
            onClick={() => setDeleteConfirm(true)}
            className="inline-flex items-center gap-2 border border-red-300 text-red-700 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Trash2 className="w-4 h-4" /> Delete my account
          </button>
        ) : (
          <div className="space-y-3">
            <input
              type="password"
              placeholder="Enter your password to confirm"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="border border-red-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 w-full md:w-80"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setDeleteConfirm(false); setDeletePassword(""); }}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteAccount}
                disabled={deleting}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Permanently delete
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
